import React from "react";
import { alpha, Box, Skeleton, Typography, useTheme } from "@mui/material";
import type { VisualizationMeta } from "hooks/useNLQuery";

interface Props {
  rows: Record<string, unknown>[];
  columns: string[];
  visualization: VisualizationMeta;
  loading?: boolean;
}

const AgenticMetricCard: React.FC<Props> = ({ rows, columns, visualization, loading }) => {
  const theme = useTheme();
  const textPrimary = theme.custom?.contentText?.primary || theme.palette.text.primary;
  const textSecondary = theme.custom?.contentText?.secondary || theme.palette.text.secondary;

  if (loading || !rows.length) {
    return <Skeleton variant="rounded" height={120} sx={{ borderRadius: 2 }} />;
  }

  const row = rows[0];
  const skipCols = new Set(["patient_id", "id", "patient_name", "name"]);
  const metrics = columns
    .filter((c) => !skipCols.has(c))
    .map((col) => ({
      label: col.replace(/_/g, " "),
      value: row[col],
    }));

  if (metrics.length === 0) return null;

  if (rows.length === 1 && metrics.length <= 6) {
    return (
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 2,
          p: 2,
        }}
      >
        {metrics.map((m) => (
          <Box
            key={m.label}
            sx={{
              flex: "1 1 120px",
              minWidth: 120,
              maxWidth: 200,
              p: 2,
              borderRadius: 2,
              background: alpha(theme.palette.primary.main, 0.06),
              border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
              textAlign: "center",
            }}
          >
            <Typography
              variant="h4"
              fontWeight={700}
              sx={{ color: theme.palette.primary.main, mb: 0.5 }}
            >
              {formatMetricValue(m.value)}
            </Typography>
            <Typography
              variant="caption"
              fontWeight={600}
              sx={{
                color: textSecondary,
                textTransform: "uppercase",
                letterSpacing: 0.5,
                fontSize: "0.65rem",
              }}
            >
              {m.label}
            </Typography>
          </Box>
        ))}
      </Box>
    );
  }

  const primary = metrics[0];
  if (!primary || primary.value == null) return null;

  return (
    <Box sx={{ p: 2, textAlign: "center" }}>
      <Typography variant="h3" fontWeight={700} sx={{ color: theme.palette.primary.main }}>
        {formatMetricValue(primary.value)}
      </Typography>
      <Typography variant="body2" sx={{ color: textSecondary, mt: 0.5 }}>
        {primary.label}
      </Typography>
    </Box>
  );
};

function formatMetricValue(value: unknown): string {
  if (value === null || value === undefined) return "-";
  if (typeof value === "number") {
    if (Number.isInteger(value)) return value.toLocaleString();
    return value.toFixed(2);
  }
  return String(value);
}

export default AgenticMetricCard;
